import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BlService } from 'src/app/_services/bl/bl.service';
import { Rule238Service } from 'src/app/_services/rule238/rule238.service';
import { BlDetails } from 'src/app/interfaces/bl.interfaces';
import { CustomsEmbargoedMerchandise, DeleteEmbargoedMerchandise, Rule238, Rule238Create, Rule238Update, UpdateEmbargoedMerchandise } from 'src/app/interfaces/rule238.interfaces';
import { Rule238FormComponent } from '../rule238-form/rule238-form.component';

enum Actions {
  Detail,
  Create,
  Update,
  Consult
}

/**
 * Componente que administra el registro de la Regla 2.3.8 de un barco: obtiene
 * los detalles del BL, consulta, crea, actualiza y elimina la regla y gestiona
 * la mercancía embargada asociada.
 */
@Component({
  selector: 'app-rule238',
  templateUrl: './rule238.component.html',
  styleUrls: ['./rule238.component.css']
})
export class Rule238Component implements OnInit {
  loading: boolean = false;
  overlayError: boolean = false;
  overlayMessage: boolean = false;
  message: string = '';
  icon: string = '';

  @ViewChild('rule238Form') rule238Form!: Rule238FormComponent;
  blDetails!: BlDetails;
  rule238!: Rule238Update;
  canDelete: boolean = false;
  Actions = Actions;
  wasCreatedUpdated: boolean = false;

  constructor(
    private rule238Svc: Rule238Service,
    private blServices: BlService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    const operationNumber = Number(this.activatedRoute.snapshot.paramMap.get('shipId'));
    this.blServices.path = `/ship-list/${operationNumber}`;
    this.getShipDetails(operationNumber);
  }

  /**
   * Obtiene los detalles del BL del barco indicado y, si es válido, consulta su Regla 2.3.8.
   * @param operationNumber - Número de operación del barco.
   */
  getShipDetails(operationNumber: number): void {
    this.loading = true;
    this.blServices.getBlId(operationNumber)
    .subscribe({
      next: (blDetails) => {
        const isBlDetailsValid = !Array.isArray(blDetails) || !blDetails.length;
        if (isBlDetailsValid) {
          this.loading = false;
          this.overlayError = true;
          return;
        }
        this.blDetails = blDetails[0];
        this.checkShipType();
        this.getRule238(this.blDetails.operation_id, this.Actions.Consult);
      }, error: () => {
        this.loading = false;
        this.overlayError = true;
      }
    });
  }

  /**
   * Consulta el registro de la Regla 2.3.8 por operación y muestra un mensaje de éxito tras crear o actualizar.
   * @param operationId - Identificador de la operación del BL.
   * @param currentAction - Acción que originó la consulta (consultar, crear o actualizar).
   */
  getRule238(operationId: number, currentAction: Actions): void {
    this.rule238Svc.get(operationId)
    .subscribe({
      next: (rule238) => {
        this.loading = false;
        const isRule238Valid = !Array.isArray(rule238);
        if (isRule238Valid) {
          this.overlayError = true;
          return;
        }
        this.canDelete = rule238.length > 0;
        this.rule238 = rule238.length > 0 ? rule238[0] : {} as Rule238Update;
        if (currentAction !== Actions.Consult) {
          this.wasCreatedUpdated = true;
          const message = currentAction === Actions.Create ? 'Regla 2.3.8 creada con éxito' : 'Regla 2.3.8 actualizada con éxito';
          this.handleOverlay(
            message,
            'bi bi-check2',
            2000
          );
        }
      }, error: () => {
        this.loading = false;
        this.overlayError = true;
      }
    });
  }

  /**
   * Redirige al detalle del barco cuando la operación es de tipo importación.
   */
  checkShipType(): void {
    if (this.blDetails.operation_type === 'IMPORTACION')
      this.navigateToShipDetails();
  }

  /**
   * Crea o actualiza la Regla 2.3.8 según la acción recibida, agregando los identificadores necesarios.
   * @param rule238 - Datos de la Regla 2.3.8 a guardar.
   * @param action - Acción a ejecutar (crear o actualizar).
   */
  public saveRule238(rule238: Rule238, action: Actions): void {
    if (action === Actions.Create) {
      const currenRule238: Rule238Create = {
        ...rule238,
        operation_id: this.blDetails.operation_id
      };
      this.createRule238(currenRule238);
    } else if (action === Actions.Update) {
      const currenRule238: Rule238Update = {
        ...rule238,
        id: this.rule238?.id || 0
      };
      this.updateRule238(currenRule238);
    }
  }

  /**
   * Crea un nuevo registro de la Regla 2.3.8 y vuelve a consultarlo al completarse.
   * @param rule238 - Datos de la Regla 2.3.8 a crear.
   */
  public createRule238(rule238: Rule238Create): void {
    this.loading = true;
    this.rule238Svc.create(rule238).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.error) {
          this.handleOverlay(
            'Error al crear la regla 2.3.8',
            'bi bi-exclamation-circle-fill',
            2000
          );
          return;
        }
        this.getRule238(this.blDetails.operation_id, Actions.Create);
      }, error: (_) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear la regla 2.3.8',
          'bi bi-exclamation-circle-fill',
          3000
        );
      }
    });
  }

  /**
   * Actualiza la Regla 2.3.8 y gestiona la actualización o eliminación de la mercancía embargada asociada.
   * @param rule238 - Datos de la Regla 2.3.8 a actualizar.
   */
  public updateRule238(rule238: Rule238Update): void {
    this.loading = true;
    const customsEmbargoedMerchandise: CustomsEmbargoedMerchandise[] = rule238.customs_embargoed_merchandise || [];
    rule238.customs_embargoed_merchandise = [];
    this.rule238Svc.update(rule238).subscribe({
      next: (response) => {
        this.loading = false;
        this.loading = false;
        if (response.error) {
          this.handleOverlay(
            'Error al actualizar la regla 2.3.8',
            'bi bi-exclamation-circle-fill',
            2000
          );
          return;
        }
        const removedIds = this.getRemovedItems(customsEmbargoedMerchandise);
        if (removedIds.length) {
          this.deleteEmbargoedMerchandise(customsEmbargoedMerchandise);
          return;
        }
        if (!removedIds.length && customsEmbargoedMerchandise.length) {
          this.updateEmbargoedMerchandise(customsEmbargoedMerchandise);
          return;
        }
        this.getRule238(this.blDetails.operation_id, Actions.Update);
      }, error: (_) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al actualizar la regla 2.3.8',
          'bi bi-exclamation-circle-fill',
          3000
        );
      }
    });
  }

  /**
   * Actualiza la mercancía embargada de la Regla 2.3.8 y vuelve a consultar el registro.
   * @param customsEmbargoedMerchandise - Lista de mercancía embargada a actualizar.
   */
  private updateEmbargoedMerchandise(customsEmbargoedMerchandise: CustomsEmbargoedMerchandise[]): void {
    const maxId = customsEmbargoedMerchandise.reduce((max, obj) => obj.id > max ? obj.id : max, 0);
    const embargoed: UpdateEmbargoedMerchandise = {
      id: this.rule238.id,
      last_id_embargoed: maxId,
      customs_embargoed_merchandise: customsEmbargoedMerchandise
    };
    this.rule238Svc.updateEmbargoed(embargoed).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.error) {
          this.handleOverlay(
            'Error al actualizar la mercancía embargada',
            'bi bi-exclamation-circle-fill',
            3000
          );
          return;
        }
        this.getRule238(this.blDetails.operation_id, Actions.Update);
      }, error: (_) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al actualizar la mercancía embargada',
          'bi bi-exclamation-circle-fill',
          3000
        );
      }
    })
  }

  /**
   * Elimina del servidor las mercancías embargadas removidas y, al terminar, actualiza las restantes o reconsulta la regla.
   * @param customsEmbargoedMerchandise - Lista actual de mercancía embargada para determinar los elementos eliminados.
   */
  private deleteEmbargoedMerchandise(customsEmbargoedMerchandise: CustomsEmbargoedMerchandise[]): void {
    const embargoedMerchandiseIds: number[] = this.getRemovedItems(customsEmbargoedMerchandise);
    const lastRemoved = embargoedMerchandiseIds.length -1;
    embargoedMerchandiseIds.forEach((embargoedId, index) => {
      const embargoedToRemove: DeleteEmbargoedMerchandise = {
        rule_register_id: this.rule238.id,
        id: embargoedId
      };
      this.rule238Svc.deleteEmbargoed(embargoedToRemove).subscribe({
        next: (response) => {
          if (response.error) {
            this.handleOverlay(
              'Error al eliminar la mercancía embargada',
              'bi bi-exclamation-circle-fill',
              3000
            );
            return;
          }
          if (lastRemoved === index) {
            if (customsEmbargoedMerchandise.length) {
              this.updateEmbargoedMerchandise(customsEmbargoedMerchandise);
              return;
            }
            this.getRule238(this.blDetails.operation_id, Actions.Update);
          }
        }, error: (_) => {
          this.handleOverlay(
            'Error en el servidor al eliminar la mercancía embargada',
            'bi bi-exclamation-circle-fill',
            3000
          );
        }
      });
    })
  }

  /**
   * Elimina el registro de la Regla 2.3.8 actual y reinicia el formulario al completarse.
   */
  public deleteRule238(): void {
    this.loading = true;
    const id: number = this.rule238?.id || 0;
    this.rule238Svc.delete(id).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.error) {
          this.handleOverlay(
            'Error al eliminar la regla 2.3.8',
            'bi bi-exclamation-circle-fill',
            2000
          );
          return;
        }
        this.handleOverlay(
          'Regla 2.3.8 eliminada con éxito',
          'bi bi-check2',
          2000
        );
        this.canDelete = false;
        this.rule238 = {} as Rule238Update;
        this.rule238Form.resetForm();
      }, error: (_) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al eliminar la regla 2.3.8',
          'bi bi-exclamation-circle-fill',
          3000
        );
      }
    });
  }

  /**
   * Calcula los identificadores de mercancía embargada que fueron removidos respecto al registro actual.
   * @param customsEmbargoedMerchandise - Lista actual de mercancía embargada.
   * @returns Los identificadores de las mercancías que ya no están presentes.
   */
  getRemovedItems(customsEmbargoedMerchandise: CustomsEmbargoedMerchandise[]): number[] {
    const lastIds = this.rule238.customs_embargoed_merchandise.map((customs) => customs.id);
    const currentIds = customsEmbargoedMerchandise.map((customs) => customs.id && customs.id > 0 ? customs.id : 0);
    const removedIds = lastIds.filter((id) => !currentIds.includes(id));
    return removedIds;
  }

  /**
   * Navega a la pantalla de detalle del barco actual.
   */
  public navigateToShipDetails(): void {
    this.router.navigate([`/ship-list/${this.blDetails.operation_number}`]);
  }

  /**
   * Muestra un mensaje en overlay con ícono durante el tiempo indicado.
   * @param message - Mensaje a mostrar.
   * @param icon - Clase del ícono a mostrar.
   * @param time - Tiempo en milisegundos antes de ocultar el mensaje.
   */
  private handleOverlay(message: string, icon: string, time: number): void {
    this.message = message;
    this.icon = icon;
    this.overlayMessage = true;
    setTimeout(() => {
      this.overlayMessage = false;
    }, time);
  }

}
